import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface CartItem {
  id: string;
  figurine_id: string;
  title: string;
  slug: string;
  price_pln: number | null;
  image_url: string | null;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  totalCount: number;
  totalPrice: number;
  loading: boolean;
  addItem: (figurineId: string, quantity?: number) => Promise<void>;
  removeItem: (figurineId: string) => Promise<void>;
  updateQuantity: (figurineId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refetch: () => Promise<void>;
}

const CartContext = createContext<CartContextType | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!user) { setItems([]); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from('cart_with_figurines')
      .select('*')
      .eq('user_id', user.id)
      .order('added_at');
    if (!error && data) {
      setItems(data.map((r: any) => ({
        id: r.id,
        figurine_id: r.figurine_id,
        title: r.title,
        slug: r.slug,
        price_pln: r.price_pln != null ? Number(r.price_pln) : null,
        image_url: r.image_url,
        quantity: r.quantity,
      })));
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addItem = async (figurineId: string, quantity = 1) => {
    if (!user) { toast.error('Zaloguj się, aby dodać do koszyka'); return; }
    const existing = items.find(i => i.figurine_id === figurineId);
    if (existing) {
      await updateQuantity(figurineId, existing.quantity + quantity);
      return;
    }
    const { error } = await supabase.from('cart_items').insert({
      user_id: user.id, figurine_id: figurineId, quantity,
    });
    if (error) { toast.error('Nie udało się dodać do koszyka'); return; }
    toast.success('Dodano do koszyka ✓');
    await fetchCart();
  };

  const removeItem = async (figurineId: string) => {
    if (!user) return;
    await supabase.from('cart_items')
      .delete()
      .eq('user_id', user.id)
      .eq('figurine_id', figurineId);
    await fetchCart();
  };

  const updateQuantity = async (figurineId: string, quantity: number) => {
    if (!user) return;
    if (quantity <= 0) { await removeItem(figurineId); return; }
    await supabase.from('cart_items')
      .update({ quantity })
      .eq('user_id', user.id)
      .eq('figurine_id', figurineId);
    await fetchCart();
  };

  const clearCart = async () => {
    if (!user) return;
    await supabase.from('cart_items').delete().eq('user_id', user.id);
    setItems([]);
  };

  const totalCount = items.reduce((s, i) => s + i.quantity, 0);
  const totalPrice = items.reduce((s, i) => s + (i.price_pln ?? 0) * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, totalCount, totalPrice, loading, addItem, removeItem, updateQuantity, clearCart, refetch: fetchCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
